/**
 * Currency Utility Module
 * 
 * Centralized currency formatting for the CFF prototype.
 * 
 * CURRENT STATE (Phase 1):
 * - Single currency: GBP (£)
 * - All amounts displayed with £ symbol
 * - All calculations in pounds
 * 
 * FUTURE STATE (Phase 3 - Multi-Currency):
 * - Multiple currencies supported
 * - Currency symbol dynamic based on account/transaction
 * - Currency conversion and rates
 * - Reporting currency selection
 * 
 * This utility is designed to be easily extended for multi-currency support
 * without requiring changes throughout the codebase.
 */

/**
 * Format currency amount with proper symbol and formatting
 * 
 * @param amount - The numeric amount to format
 * @param currency - Currency code (default: 'GBP'). Future: will support EUR, USD, etc.
 * @param options - Optional formatting options
 * @returns Formatted currency string (e.g., "£1,234.56")
 * 
 * @example
 * formatCurrency(1234.56) // "£1,234.56"
 * formatCurrency(1234.56, 'GBP', { compact: true }) // "£1.2K"
 * formatCurrency(1234567, 'GBP', { compact: true }) // "£1.2M"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: string = 'GBP',
  options?: {
    compact?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  // Handle null/undefined amounts
  if (amount === null || amount === undefined) {
    return '£0.00';
  }

  // For now, always use GBP formatting
  // Future: Will handle multiple currencies based on currency parameter
  const locale = 'en-GB';
  const currencyCode = 'GBP'; // Future: Will use currency parameter

  // Compact notation for large numbers (£1.2M instead of £1,200,000)
  if (options?.compact) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(amount);
  }

  // Standard currency formatting
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: options?.minimumFractionDigits ?? 2,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(amount);
}

/**
 * Format currency in compact notation (£1.2M instead of £1,200,000)
 * 
 * @param amount - The numeric amount to format
 * @param currency - Currency code (default: 'GBP')
 * @returns Formatted currency string in compact notation
 * 
 * @example
 * formatCurrencyCompact(1200000) // "£1.2M"
 * formatCurrencyCompact(1500) // "£1.5K"
 */
export function formatCurrencyCompact(
  amount: number | null | undefined,
  currency: string = 'GBP'
): string {
  return formatCurrency(amount, currency, { compact: true });
}

/**
 * Get currency symbol for a given currency code
 * 
 * @param currency - Currency code (default: 'GBP')
 * @returns Currency symbol (e.g., "£")
 * 
 * @example
 * getCurrencySymbol('GBP') // "£"
 * getCurrencySymbol('EUR') // "€" (future)
 * getCurrencySymbol('USD') // "$" (future)
 */
export function getCurrencySymbol(currency: string = 'GBP'): string {
  // For now, always return GBP symbol
  // Future: Will return proper symbol for any currency
  
  // Map of currency codes to symbols (for future use)
  const currencySymbols: Record<string, string> = {
    GBP: '£',
    EUR: '€',
    USD: '$',
    CAD: 'CA$',
    AUD: 'A$',
    NZD: 'NZ$',
    JPY: '¥',
    CNY: '¥',
    INR: '₹',
    CHF: 'CHF',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RON: 'lei',
    BGN: 'лв',
    HRK: 'kn',
    RUB: '₽',
    TRY: '₺',
    ZAR: 'R',
    BRL: 'R$',
    MXN: 'MX$',
    ARS: 'AR$',
    CLP: 'CL$',
    COP: 'CO$',
    PEN: 'S/',
    SGD: 'S$',
    HKD: 'HK$',
    KRW: '₩',
    THB: '฿',
    MYR: 'RM',
    IDR: 'Rp',
    PHP: '₱',
    VND: '₫',
    AED: 'د.إ',
    SAR: '﷼',
    ILS: '₪',
    EGP: 'E£',
  };

  return currencySymbols[currency.toUpperCase()] || '£';
}

/**
 * Format currency for input fields (no symbol, just number)
 * 
 * @param amount - The numeric amount to format
 * @returns Formatted number string without currency symbol
 * 
 * @example
 * formatCurrencyInput(1234.56) // "1234.56"
 */
export function formatCurrencyInput(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '0.00';
  }

  return amount.toFixed(2);
}

/**
 * Parse currency input string to number
 * 
 * @param value - String value from input field
 * @returns Parsed number value
 * 
 * @example
 * parseCurrencyInput("1234.56") // 1234.56
 * parseCurrencyInput("£1,234.56") // 1234.56
 * parseCurrencyInput("1.2k") // 1200
 * parseCurrencyInput("1.2m") // 1200000
 */
export function parseCurrencyInput(value: string): number {
  if (!value) return 0;

  // Remove currency symbols and whitespace
  let cleaned = value.replace(/[£$€,\s]/g, '');

  // Handle k/K suffix (thousands)
  if (cleaned.toLowerCase().endsWith('k')) {
    const num = parseFloat(cleaned.slice(0, -1));
    return isNaN(num) ? 0 : num * 1000;
  }

  // Handle m/M suffix (millions)
  if (cleaned.toLowerCase().endsWith('m')) {
    const num = parseFloat(cleaned.slice(0, -1));
    return isNaN(num) ? 0 : num * 1000000;
  }

  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Format currency for chart axes and tooltips
 * 
 * @param value - The numeric value to format
 * @param compact - Whether to use compact notation (default: true)
 * @returns Formatted currency string suitable for charts
 * 
 * @example
 * formatCurrencyForChart(1234567) // "£1.2M"
 * formatCurrencyForChart(1234, false) // "£1,234"
 */
export function formatCurrencyForChart(
  value: number,
  compact: boolean = true
): string {
  if (compact) {
    return formatCurrencyCompact(value);
  }
  return formatCurrency(value, 'GBP', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/**
 * Format currency for CSV/Excel exports (numeric value only, no symbol)
 * 
 * @param amount - The numeric amount
 * @returns Number as string with 2 decimal places
 * 
 * @example
 * formatCurrencyForExport(1234.56) // "1234.56"
 */
export function formatCurrencyForExport(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) {
    return '0.00';
  }
  return amount.toFixed(2);
}

/**
 * Get default currency for the application
 * 
 * @returns Default currency code
 * 
 * @example
 * getDefaultCurrency() // "GBP"
 */
export function getDefaultCurrency(): string {
  // For now, always return GBP
  // Future: Will read from user preferences or company settings
  return 'GBP';
}

/**
 * Validate if a currency code is supported
 * 
 * @param currency - Currency code to validate
 * @returns True if currency is supported
 * 
 * @example
 * isSupportedCurrency('GBP') // true
 * isSupportedCurrency('EUR') // false (in Phase 1)
 */
export function isSupportedCurrency(currency: string): boolean {
  // For now, only GBP is supported
  // Future: Will check against list of supported currencies
  return currency.toUpperCase() === 'GBP';
}
